import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { MyBonusesPanel } from "@/components/administration/MyBonusesPanel";
import { CelebrationsCard, AnnouncementsCard, AttendanceRankingCard } from "@/components/intranet/IntranetWidgets";
import { QuickAccessBar, NewsSection, DocumentCenter, TrainingSection, LinksSection, WellnessSection, ProjectsSection, ForumSection } from "@/components/intranet/IntranetSections";
import { SubmitServicePanel } from "@/components/intranet/SubmitServicePanel";
import { Clock, TrendUp, Users, Briefcase, Heart, Wrench } from "@phosphor-icons/react";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

export default function TeamDashboard() {
  const { user, token } = useAuth();
  const [myStatus, setMyStatus] = useState(null);
  const [teamStats, setTeamStats] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/my-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMyStatus(r.data))
      .catch(e => process.env.NODE_ENV === "development" && console.error(e.message));
    if (user?.role === "admin") {
      axios.get(`${API}/tickets/stats/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setTeamStats(r.data))
        .catch(e => process.env.NODE_ENV === "development" && console.error(e.message));
    }
  }, [token, user]);

  const now = new Date();
  const monthName = MONTHS[now.getMonth()];
  const greeting = now.getHours() < 12 ? "Buenos dias" : now.getHours() < 18 ? "Buenas tardes" : "Buenas noches";

  return (
    <div className="space-y-6" data-testid="team-dashboard">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-800 via-slate-700 to-blue-800 rounded-2xl overflow-hidden" data-testid="team-hero">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.4\"%3E%3Cpath d=\"M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')" }} />
        <div className="relative px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-white tracking-tight">TEAM</h1>
              <p className="text-blue-200 text-lg mt-1">{greeting}, <span className="font-semibold text-white">{user?.name}</span></p>
              <p className="text-blue-300/70 text-sm mt-0.5">{monthName} {now.getFullYear()}</p>
            </div>
            <div className="flex items-center gap-4">
              {myStatus && (
                <div className="grid grid-cols-2 gap-3">
                  {myStatus.tickets_count !== undefined && (
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 text-center border border-white/10">
                      <p className="text-2xl font-black text-white">{myStatus.tickets_count || 0}</p>
                      <p className="text-[10px] text-blue-200 uppercase font-semibold tracking-wider">Tickets</p>
                    </div>
                  )}
                  {myStatus.attendance_days !== undefined && (
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 text-center border border-white/10">
                      <p className="text-2xl font-black text-white">{myStatus.attendance_days || 0}</p>
                      <p className="text-[10px] text-blue-200 uppercase font-semibold tracking-wider">Asistencia</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats for Admin */}
      {user?.role === "admin" && teamStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="team-admin-stats">
          <StatCard icon={Briefcase} label="Tickets Abiertos" value={teamStats.open_tickets || 0} color="blue" />
          <StatCard icon={Clock} label="Pendientes" value={teamStats.by_status?.pendiente || teamStats.by_status?.en_espera || 0} color="amber" />
          <StatCard icon={TrendUp} label="Resueltos (Mes)" value={teamStats.resolved_tickets || 0} color="emerald" />
          <StatCard icon={Users} label="Ingenieros Activos" value={teamStats.engineer_stats?.length || 0} color="violet" />
        </div>
      )}

      {/* Submit Service / Bonuses panels for employees */}
      {["ingeniero", "comercial"].includes(user?.role) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <MyBonusesPanel />
          <SubmitServicePanel />
        </div>
      )}

      {/* Main Grid: Ranking LEFT + Celebrations RIGHT */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Left: Ranking (full height) */}
        <div className="lg:col-span-3">
          <AttendanceRankingCard />
        </div>

        {/* Right: Celebrations */}
        <div className="lg:col-span-2">
          <CelebrationsCard />
        </div>
      </div>

      {/* Announcements */}
      <AnnouncementsCard />

      {/* Quick Access Icons */}
      <div className="bg-white rounded-xl border p-5">
        <QuickAccessBar onNavigate={(id) => {
          const el = document.getElementById(`section-${id}`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }} />
      </div>

      {/* News + Document Center */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3"><NewsSection /></div>
        <div className="lg:col-span-2"><DocumentCenter /></div>
      </div>

      {/* Services + Tools */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <LinksSection type="services" title="Servicios al Trabajador" icon={Briefcase} color="bg-emerald-600" apiPath="services" />
        <LinksSection type="tools" title="Herramientas del Dia a Dia" icon={Wrench} color="bg-slate-600" apiPath="tools" />
      </div>

      {/* Training + Wellness */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <TrainingSection />
        <WellnessSection />
      </div>

      {/* Projects + Forum */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ProjectsSection />
        <ForumSection />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || colors.blue}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} weight="duotone" />
        <p className="text-[10px] uppercase font-bold tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-black">{value}</p>
    </div>
  );
}
